import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, ModalMultiUse, Paragraph, Headings } from '@sentara-health/optimahealth_web';
import { DeviceTypes, useDeviceType } from 'common';

export type AuthorizationAccessType = 'spouse' | 'parent';

type AuthorizationConsentModalProps = {
  isOpen: boolean;
  accessType: AuthorizationAccessType;
  onClose: () => void;
  onConfirm: () => void;
  showActionButtons?: boolean;
};

type RemoveAccessModalProps = {
  isOpen: boolean;
  accessType: AuthorizationAccessType | null;
  onClose: () => void;
  onConfirm: () => void;
};

type AuthorizationErrorModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const AuthorizationConsentModal: React.FC<AuthorizationConsentModalProps> = ({
  isOpen,
  accessType,
  onClose,
  onConfirm,
  showActionButtons = true,
}) => {
  const { t } = useTranslation();
  const [isConsentEnabled, setIsConsentEnabled] = useState(false);
  const termsRef = useRef<HTMLDivElement | null>(null);

  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;

  const relation = accessType === 'spouse' ? 'spouse' : 'parent/guardian';
  const relationTitle = accessType === 'spouse' ? 'Spouse' : 'Parent/Guardian';

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const reachedBottom = Math.ceil(scrollTop + clientHeight) >= Math.floor(scrollHeight);

    if (reachedBottom) {
      setIsConsentEnabled(true);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setIsConsentEnabled(false);
      return;
    }

    if (!termsRef.current) {
      return;
    }

    termsRef.current.scrollTop = 0;

    const { scrollHeight, clientHeight } = termsRef.current;
    if (scrollHeight <= clientHeight) {
      setIsConsentEnabled(true);
    }
  }, [isOpen]);

  return (
    <ModalMultiUse
      isOpen={isOpen}
      onClose={onClose}
      header={t('authorizations.consentModalTitle', { relationTitle })}
      body={
        <>
          {showActionButtons ? (
          <Paragraph
           text={t('authorizations.consentModalInstruction', { relationTitle })}
              variant="bold"
              level={3}
            />
          ) : null}
          <div
            className="border rounded modal-body authorization-wrapper"
            onScroll={handleScroll}
            ref={termsRef}
            data-testid="parentConsentTermsContainer"
          >
            <Paragraph
              text={
                <Trans
                  i18nKey="authorizations.consentModalBody"
                  values={{ relation }}
                />
              }
              variant="regular"
              className="mb-0"
            />
          </div>
        </> 
      }
      footer={
        showActionButtons ? (
          <div className="row">
            <div className="col-6 d-grid">
              <Button
                variant="primary_btn"
                onClick={onConfirm}
                disabled={!isConsentEnabled}
                dataTestId="parentConsentConfirmButton"
                className="d-block rounded-pill py-2"
              >
                {t('authorizations.consent')}
              </Button>
            </div>
            <div className="col-6 d-grid">
              <Button
                variant="secondary_btn"
                onClick={onClose}
                dataTestId="parentConsentCancelButton"
                className="d-block rounded-pill py-2"
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : <div className="col-4 d-grid">
              <Button
                variant="secondary_btn"
                onClick={onClose}
                dataTestId="parentConsentCancelButton"
                className="d-block rounded-pill py-2"
              >
                {t('common.close')}
              </Button>
            </div>
      }
      variant="defaultNoPbBody"
      className={`${isMobile ? '' : 'authorization-modal-width'}`}
      modalId="authorizationConsentModal"
    />
  );
};

export const RemoveAccessModal: React.FC<RemoveAccessModalProps> = ({
  isOpen,
  accessType,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();

  const relation = accessType === 'parent' ? 'parent/guardian' : 'spouse';

  return (
    <ModalMultiUse
      isOpen={isOpen}
      onClose={onClose}
      header={t('authorizations.removeSharedAccessTitle')}
      body={
        <>
          <Paragraph
            text={
              <Trans
                i18nKey="authorizations.removeAccessDescription"
                values={{ relation }}
              />
            }
            variant="regular"
            className="mb-3"
          />
          <Paragraph
            text={t('authorizations.keepAccessDescription')}
            variant="regular"
            className="mb-0"
          />
        </>
      }
      footer={
        <div className="row">
          <div className="col-md-6 col-12 mb-md-0 mb-3 ps-sm-0 pe-sm-2 px-0 d-grid">
            <Button
              variant="primary_btn"
              onClick={onConfirm}
              dataTestId="removeAccessConfirmButton"
              className="d-block rounded-pill py-2"
            >
              {t('authorizations.removeAccess')}
            </Button>
          </div>
          <div className="col-md-6 col-12 ps-sm-2 pe-sm-0 px-0 d-grid">
            <Button
              variant="secondary_btn"
              onClick={onClose}
              dataTestId="removeAccessCancelButton"
              className="d-block rounded-pill py-2"
            >
              {t('authorizations.keepAccess')}
            </Button>
          </div>
        </div>
      }
      variant="defaultNoPyBody"
      className=""
      modalId="removeAccessModal"
    />
  );
};

export const AuthorizationErrorModal: React.FC<AuthorizationErrorModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <ModalMultiUse
      isOpen={isOpen}
      onClose={onClose}
      body={
        <div className="pt-1">
          <Headings text={t('common.error')} level="3" className="mb-3" />
          <Paragraph
            text={t('authorizations.errorMessageDescription')}
            variant="regular"
            className="mb-0"
          />
        </div>
      }
      footer={
        <div className="row">
          <div className="col-12 d-grid">
            <Button
              variant="primary_btn"
              onClick={onClose}
              dataTestId="authorizationErrorModalOkButton"
              className="d-block rounded-pill py-2 w-100 text-uppercase"
            >
              {t('common.ok')}
            </Button>
          </div>
        </div>
      }
      variant="defaultNoPbBody"
      className=""
      modalId="authorizationErrorModal"
    />
  );
};
